/**
 * ProfileScreen - Premium agent detail view matching v0 design
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Agent, Review } from '../types/agent';
import * as api from '../lib/api';
import { hapticFeedback, hapticNotification } from '../lib/telegram';
import { showToast } from '../components/Toast';
import { postEvent } from '@telegram-apps/sdk-react';
import { trackEvent, getStats, type AnalyticsStats } from '../lib/analytics';

interface ProfileScreenProps {
  agentId: string;
  onBack: () => void;
  onEdit?: (agent: Agent) => void;
}

import { getCategoryLabel } from '../lib/categories';

function daysAgo(timestamp: number): string {
  const now = Date.now();
  const created = timestamp * 1000;
  const diffMs = now - created;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Listed today';
  if (diffDays === 1) return 'Listed yesterday';
  return `Listed ${diffDays} days ago`;
}

function getShareUrl(agentId: string): string {
  return `https://t.me/tele_claw_bot/app?startapp=agent_${agentId}`;
}

async function shareAgent(agent: Agent): Promise<void> {
  const shareUrl = getShareUrl(agent.id);
  const shareText = `Check out ${agent.name} on TeleClaw!`;
  const webApp = window.Telegram?.WebApp as { openTelegramLink?: (url: string) => void } | undefined;
  if (webApp?.openTelegramLink) {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    webApp.openTelegramLink(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`);
    return;
  }
  try {
    await navigator.clipboard.writeText(shareUrl);
    hapticNotification('success');
  } catch (err) {
    console.error('Failed to copy:', err);
    hapticNotification('error');
  }
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    hapticNotification('success');
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    hapticNotification('error');
    return false;
  }
}

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          aria-label={`Rate ${star} out of 5`}
          className={`text-2xl transition-colors ${star <= value ? 'text-yellow-400' : 'text-gray-600'} focus-visible:ring-2 focus-visible:ring-[#0098EA] outline-none`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function getReferralBadge(count: number): string {
  if (count >= 10) return '🥇';
  if (count >= 5) return '🥈';
  if (count >= 3) return '🥉';
  return '';
}

export function ProfileScreen({ agentId, onBack, onEdit }: ProfileScreenProps) {
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voting, setVoting] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const viewTracked = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchAgent() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getAgent(agentId);
        if (!cancelled) setAgent(data);
      } catch (err) {
        if (!cancelled) {
          if (err instanceof api.ApiError && err.status === 404) setError('Agent not found');
          else setError('Failed to load agent');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAgent();
    return () => { cancelled = true; };
  }, [agentId]);

  useEffect(() => {
    if (agentId && !viewTracked.current) {
      viewTracked.current = true;
      trackEvent(agentId, 'view');
    }
  }, [agentId]);

  useEffect(() => {
    let cancelled = false;
    async function fetchStats() {
      try {
        const data = await getStats(agentId);
        if (!cancelled) setStats(data);
      } catch { }
    }
    fetchStats();
    return () => { cancelled = true; };
  }, [agentId]);

  useEffect(() => {
    let cancelled = false;
    async function fetchReviews() {
      try {
        const data = await api.getReviews(agentId);
        if (!cancelled) setReviews(data.reviews);
      } catch { }
    }
    fetchReviews();
    return () => { cancelled = true; };
  }, [agentId]);

  useEffect(() => {
    let cancelled = false;
    async function fetchReferral() {
      try {
        const data = await api.getReferralCode(agentId);
        if (!cancelled) setReferralCode(data.referral_code);
      } catch { }
    }
    fetchReferral();
    return () => { cancelled = true; };
  }, [agentId]);

  const handleSubmitReview = useCallback(async () => {
    if (submittingReview) return;
    setSubmittingReview(true);
    try {
      await api.submitReview(agentId, { rating: reviewRating, comment: reviewComment || undefined });
      hapticNotification('success');
      setShowReviewForm(false);
      setReviewComment('');
      const [revData, agentData] = await Promise.all([api.getReviews(agentId), api.getAgent(agentId)]);
      setReviews(revData.reviews);
      setAgent(agentData);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 409) {
        hapticNotification('error');
        showToast('You have already reviewed this app', 'error');
      } else {
        hapticNotification('error');
      }
    } finally {
      setSubmittingReview(false);
    }
  }, [agentId, reviewRating, reviewComment, submittingReview]);

  const handleShareReferral = useCallback(async () => {
    if (!referralCode) return;
    const text = `Check out this agent on TeleClaw! Use my referral: ${referralCode}`;
    try {
      await navigator.clipboard.writeText(text);
      hapticNotification('success');
      setCopied('referral');
      setTimeout(() => setCopied(null), 2000);
    } catch {
      hapticNotification('error');
    }
  }, [referralCode]);

  const handleVote = useCallback(async () => {
    if (!agent || voting) return;
    hapticFeedback('medium');
    setVoting(true);
    try {
      if (agent.hasVoted) {
        const result = await api.unvote(agent.id);
        setAgent({ ...agent, hasVoted: false, vote_count: result.vote_count });
      } else {
        const result = await api.vote(agent.id);
        setAgent({ ...agent, hasVoted: true, vote_count: result.vote_count });
      }
      hapticNotification('success');
    } catch (err) {
      console.error('Vote failed:', err);
      hapticNotification('error');
    } finally {
      setVoting(false);
    }
  }, [agent, voting]);

  const handleShare = useCallback(async () => {
    if (!agent) return;
    hapticFeedback('light');
    trackEvent(agent.id, 'share');
    await shareAgent(agent);
  }, [agent]);

  const handleEdit = useCallback(() => {
    if (!agent || !onEdit) return;
    hapticFeedback('light');
    onEdit(agent);
  }, [agent, onEdit]);

  const handleCopyWallet = useCallback(async () => {
    if (!agent?.ton_wallet) return;
    hapticFeedback('light');
    const success = await copyToClipboard(agent.ton_wallet);
    if (success) {
      setCopied('wallet');
      setTimeout(() => setCopied(null), 2000);
    }
  }, [agent?.ton_wallet]);

  const handleVisit = useCallback(() => {
    if (!agent) return;
    hapticFeedback('light');
    trackEvent(agent.id, 'click');
    if (agent.telegram_bot) {
      const botUsername = agent.telegram_bot.replace(/^@/, '');
      if ((agent as any).has_mini_app) {
        try { postEvent('web_app_open_tg_link', { path_full: `/${botUsername}/app` }); return; } catch { }
      }
      try { postEvent('web_app_open_tg_link', { path_full: `/${botUsername}` }); return; } catch { }
    }
    if (agent.url) window.open(agent.url, '_blank');
  }, [agent]);

  const handleOpenBot = useCallback(() => {
    if (!agent?.telegram_bot) return;
    hapticFeedback('light');
    const botUrl = `https://t.me/${agent.telegram_bot.replace(/^@/, '')}`;
    window.open(botUrl, '_blank');
  }, [agent?.telegram_bot]);

  const handleTwitter = useCallback(() => {
    if (!agent?.twitter) return;
    hapticFeedback('light');
    const handle = agent.twitter.replace(/^@/, '');
    window.open(`https://twitter.com/${handle}`, '_blank');
  }, [agent?.twitter]);

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-20 h-20 rounded-full bg-[#242424] animate-pulse mb-4" />
        <div className="w-32 h-6 bg-[#242424] rounded animate-pulse mb-2" />
        <div className="w-48 h-4 bg-[#242424] rounded animate-pulse" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-4xl mb-4">😿</p>
        <h2 className="text-lg font-semibold text-white mb-2">{error}</h2>
        <p className="text-[#a0a0a0] text-sm mb-6">
          The app you're looking for doesn't exist or was removed.
        </p>
        <button
          onClick={onBack}
          className="px-6 py-2 bg-[#0098EA] text-white rounded-xl font-medium"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!agent) return null;

  const categoryLabel = getCategoryLabel(agent.category);

  return (
    <div className="pb-24 animate-screen-fade">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#0098EA]/10 to-transparent p-6 space-y-4 -mx-4 px-4">
        {/* Edit and Share buttons */}
        <div className="flex justify-end gap-2">
          {onEdit && agent.isOwner && (
            <button
              onClick={handleEdit}
              className="p-2 rounded-lg bg-[#242424] border border-white/5 hover:border-white/10 transition-colors"
              aria-label="Edit"
            >
              <svg className="w-5 h-5 text-[#a0a0a0]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          <button
            onClick={handleShare}
            className="p-2 rounded-lg bg-[#242424] border border-white/5 hover:border-white/10 transition-colors"
            aria-label="Share"
          >
            <svg className="w-5 h-5 text-[#a0a0a0]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>
        </div>

        {/* Logo and Name */}
        <div className="flex items-start gap-4">
          <div className="w-20 h-20 rounded-full bg-[#0098EA] flex items-center justify-center flex-shrink-0 shadow-lg overflow-hidden">
            {agent.logo ? (
              <img src={agent.logo} alt={agent.name} className="w-full h-full object-cover" onError={(e) => {
                const t = e.target as HTMLImageElement; t.style.display = 'none'; t.parentElement!.innerHTML = '<span class="text-3xl font-bold text-white">🤖</span>';
              }} />
            ) : (
              <span className="text-3xl font-bold text-white">{agent.name[0]}</span>
            )}
          </div>
          <div className="flex-1 pt-1">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-white">{agent.name}</h1>
              {agent.verification_status === 'verified' && (
                <svg className="w-6 h-6 text-[#0098EA] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
              )}
              {agent.verification_status === 'featured' && (
                <span className="text-yellow-400 text-xl">⭐</span>
              )}
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#0098EA]/10 text-[#0098EA] border border-[#0098EA]/20">
              {categoryLabel}
            </span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="flex gap-4 justify-around bg-[#242424]/50 rounded-xl p-4 border border-white/5">
          <button onClick={handleVote} disabled={voting} className="text-center transition-all active:scale-95">
            <div className="flex items-center justify-center gap-1 mb-1">
              <svg className={`w-4 h-4 ${agent.hasVoted ? 'text-[#0098EA]' : 'text-[#a0a0a0]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
              <span className="text-xl font-bold text-white">{agent.vote_count.toLocaleString()}</span>
            </div>
            <span className="text-xs text-[#a0a0a0]">Votes</span>
          </button>
          {stats && stats.views > 0 && (
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <svg className="w-4 h-4 text-[#a0a0a0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="text-xl font-bold text-white">{stats.views.toLocaleString()}</span>
              </div>
              <span className="text-xs text-[#a0a0a0]">Views</span>
            </div>
          )}
          {agent.review_count !== undefined && agent.review_count > 0 && (
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <span className="text-yellow-400 text-sm">⭐</span>
                <span className="text-xl font-bold text-white">{(agent.avg_rating || 0).toFixed(1)}</span>
              </div>
              <span className="text-xs text-[#a0a0a0]">{agent.review_count} reviews</span>
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 px-0 pt-6 space-y-6">
        {/* About */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">About</h2>
          <p className="text-[#a0a0a0] leading-relaxed">{agent.description}</p>
        </div>

        {/* Timestamp */}
        <p className="text-sm text-[#a0a0a0]">{daysAgo(agent.created_at)}</p>

        {/* Tags */}
        {agent.tags && agent.tags.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-white mb-3">Tags</h2>
            <div className="flex flex-wrap gap-2">
              {agent.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-[#242424] text-white text-sm rounded-lg border border-white/5"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Links */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-white mb-3">Links</h2>

          {agent.telegram_bot && (
            <button
              onClick={handleOpenBot}
              className="w-full flex items-center gap-3 bg-[#242424] border border-white/5 text-white hover:bg-white/5 h-12 rounded-xl px-4 transition-colors"
            >
              <svg className="w-4 h-4 text-[#a0a0a0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>Telegram Bot</span>
              <span className="text-sm text-[#a0a0a0] ml-auto">@{agent.telegram_bot.replace(/^@/, '')}</span>
            </button>
          )}

          {agent.twitter && (
            <button
              onClick={handleTwitter}
              className="w-full flex items-center gap-3 bg-[#242424] border border-white/5 text-white hover:bg-white/5 h-12 rounded-xl px-4 transition-colors"
            >
              <span className="text-[#a0a0a0] text-sm font-bold">𝕏</span>
              <span>Twitter</span>
              <span className="text-sm text-[#a0a0a0] ml-auto">@{agent.twitter.replace(/^@/, '')}</span>
            </button>
          )}

          {agent.ton_wallet && (
            <button
              onClick={handleCopyWallet}
              className="w-full flex items-center gap-3 bg-[#242424] border border-white/5 text-white hover:bg-white/5 h-12 rounded-xl px-4 transition-colors"
            >
              <span className="text-sm">💎</span>
              <span>TON Wallet</span>
              <span className="text-sm text-[#a0a0a0] ml-auto">
                {copied === 'wallet' ? '✓ Copied!' : `${agent.ton_wallet.slice(0, 8)}...${agent.ton_wallet.slice(-6)}`}
              </span>
            </button>
          )}
        </div>

        {/* Open App CTA */}
        <button
          onClick={handleVisit}
          className="w-full bg-[#0098EA] hover:bg-[#0098EA]/90 text-white h-12 rounded-xl font-semibold text-base shadow-lg active:scale-[0.98] transition-transform"
        >
          {(agent as any).has_mini_app ? 'Open App' : 'Open Bot'}
        </button>

        {/* Referral section */}
        {referralCode && (
          <div className="bg-[#242424] border border-white/5 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-white flex items-center gap-2">
                🔗 Referral Code
                {agent.referral_count !== undefined && agent.referral_count > 0 && (
                  <span className="text-xs bg-[#0098EA]/20 text-[#0098EA] px-2 py-0.5 rounded-full">
                    {getReferralBadge(agent.referral_count)} Referred {agent.referral_count} app{agent.referral_count !== 1 ? 's' : ''}
                  </span>
                )}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-[#1a1a1a] px-3 py-2 rounded-lg text-[#a0a0a0]">
                {referralCode}
              </code>
              <button
                onClick={handleShareReferral}
                className="px-3 py-2 bg-[#0098EA] text-white text-sm rounded-lg"
              >
                {copied === 'referral' ? '✓ Copied!' : 'Share'}
              </button>
            </div>
          </div>
        )}

        {/* Reviews section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-lg text-white">Reviews</h3>
            <button
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="px-3 py-1.5 text-sm bg-[#0098EA] text-white rounded-lg"
            >
              {showReviewForm ? 'Cancel' : '✍️ Write Review'}
            </button>
          </div>

          {showReviewForm && (
            <div className="bg-[#242424] border border-white/5 rounded-xl p-4 mb-4">
              <div className="mb-3">
                <label className="text-sm text-[#a0a0a0] mb-1 block">Rating</label>
                <StarPicker value={reviewRating} onChange={setReviewRating} />
              </div>
              <div className="mb-3">
                <label className="text-sm text-[#a0a0a0] mb-1 block">Comment (optional)</label>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="What do you think of this app?"
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2 bg-[#1a1a1a] text-white placeholder-[#a0a0a0] rounded-xl resize-none outline-none focus:ring-1 focus:ring-[#0098EA] border border-white/5"
                />
              </div>
              <button
                onClick={handleSubmitReview}
                disabled={submittingReview}
                className="w-full px-4 py-2.5 bg-[#0098EA] text-white rounded-xl font-medium disabled:opacity-50"
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          )}

          {reviews.length === 0 ? (
            <p className="text-center text-[#a0a0a0] text-sm py-4">
              No reviews yet. Be the first to review!
            </p>
          ) : (
            <div className="space-y-3">
              {reviews.map((review) => (
                <div key={review.id} className="bg-[#242424] border border-white/5 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-white">{review.reviewer_name || 'Anonymous'}</span>
                    <span className="text-yellow-400 text-sm">
                      {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                    </span>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-[#a0a0a0]">{review.comment}</p>
                  )}
                  <p className="text-xs text-[#a0a0a0] opacity-50 mt-1">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
